import React, { useRef, useCallback, HTMLAttributes } from "react"
import { useVirtualizer } from "@tanstack/react-virtual"
import { Company } from "~/types/company"
import {
  CompanyItemRoot,
  CompanyItemCheckbox,
  CompanyItemContent,
  CompanyItemHeader,
  CompanyItemLogo,
  CompanyItemInfo,
  CompanyItemName,
  CompanyItemLinks,
  CompanyItemEmailCount,
  CompanyItemStatus,
  CompanyItemMeta,
  CompanyItemMetaList,
} from "./company-item"
import { LoadingSpinner } from "~/react/ui/loading-spinner"
import { cn } from "~/lib/utils"
import { Separator } from "~/react/ui/separator"

interface CompaniesListProps {
  companies: Company[]
  selectedIds: number[]
  onSelect: (ids: number[]) => void
  isLoading: boolean
  emptyMessage?: string
  onClearFilters?: () => void
}

const ESTIMATED_COMPANY_SIZE = 120

export const CompaniesList: React.FC<
  CompaniesListProps & HTMLAttributes<HTMLDivElement>
> = ({
  className,
  companies,
  selectedIds,
  onSelect,
  isLoading,
  emptyMessage,
  onClearFilters,
}) => {
  const parentRef = useRef<HTMLDivElement>(null)

  const virtualizer = useVirtualizer({
    count: companies.length,
    getScrollElement: () => parentRef.current,
    estimateSize: useCallback(() => ESTIMATED_COMPANY_SIZE, []),
    overscan: 5,
  })

  const handleSelect = useCallback(
    (id: number) => {
      onSelect([id])
    },
    [onSelect],
  )

  if (isLoading && companies.length === 0) {
    return (
      <div className="companies-list companies-list--loading">
        <LoadingSpinner />
      </div>
    )
  }

  if (companies.length === 0) {
    return (
      <div className="companies-list companies-list--empty">
        <p>{emptyMessage || "No companies found"}</p>
        {onClearFilters && (
          <button className="btn btn-primary" onClick={onClearFilters}>
            Clear filters
          </button>
        )}
      </div>
    )
  }

  return (
    <div
      className={cn("companies-list", className)}
      ref={parentRef}
      style={{ height: "100%", overflow: "auto" }}
    >
      <div
        style={{
          height: `${virtualizer.getTotalSize()}px`,
          width: "100%",
          position: "relative",
        }}
      >
        {virtualizer.getVirtualItems().map((virtualItem) => {
          const company = companies[virtualItem.index]
          if (!company) return null

          const selected = selectedIds.includes(company.id)

          return (
            <div
              key={virtualItem.key}
              data-index={virtualItem.index}
              ref={virtualizer.measureElement}
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                transform: `translateY(${virtualItem.start}px)`,
              }}
            >
              <CompanyItemRoot
                company={company}
                selected={selected}
                onSelect={handleSelect}
              >
                <CompanyItemCheckbox onSelect={handleSelect} />
                <CompanyItemContent>
                  <CompanyItemHeader>
                    <CompanyItemLogo />
                    <CompanyItemInfo>
                      <CompanyItemName />
                      <Separator orientation="vertical" />
                      <CompanyItemLinks />
                      <Separator orientation="vertical" />
                      <CompanyItemEmailCount />
                    </CompanyItemInfo>
                  </CompanyItemHeader>
                  {(company.size || company.category || company.address) && (
                    <CompanyItemMetaList>
                      {company.size && (
                        <CompanyItemMeta icon="users">
                          {company.size}
                        </CompanyItemMeta>
                      )}
                      {company.category && (
                        <CompanyItemMeta icon="buildings">
                          {company.category}
                        </CompanyItemMeta>
                      )}
                      {company.address && (
                        <CompanyItemMeta icon="map-location-dot">
                          {company.address}
                        </CompanyItemMeta>
                      )}
                    </CompanyItemMetaList>
                  )}
                </CompanyItemContent>
              </CompanyItemRoot>
            </div>
          )
        })}
        {isLoading && <LoadingSpinner />}
      </div>
    </div>
  )
}
